import { memo } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, XIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Inline status badge for a COMPLETED `request_commit_approval` tool call (Phase 1 BUG 2 fix).
 * Replaces the generic "Tool used: request_commit_approval" pill that ToolFallback would
 * otherwise render. The backend writes one of three sentinel shapes to the
 * ToolCallMessagePart.Content on the result endpoint (ADR-042):
 *
 *   - `approved tempFileId={previewId}` — user clicked Approve in the sidebar
 *   - `rejected`                          — user clicked Reject with no reason
 *   - `rejected: <reason>`                — user clicked Reject with a reason
 *
 * Anything else (empty string, malformed) renders nothing so a regression in the
 * backend sentinel format doesn't surface an ugly raw string in the chat history.
 */
export type RequestCommitApprovalStatusBadgeProps = {
  content: string;
};

const APPROVED_PREFIX = "approved tempFileId=";
const REJECTED_BARE = "rejected";
const REJECTED_WITH_REASON = /^rejected:\s*(.+)$/s;

export const RequestCommitApprovalStatusBadge = memo(function RequestCommitApprovalStatusBadge(
  { content }: RequestCommitApprovalStatusBadgeProps,
) {
  const { t } = useTranslation();
  if (!content) return null;

  if (content.startsWith(APPROVED_PREFIX)) {
    return (
      <div
        className={cn(
          "aui-approval-status flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800",
        )}
        data-testid="approval-status-approved"
      >
        <CheckIcon className="size-3.5" />
        <span className="font-medium">{t("approval.status.approved")}</span>
      </div>
    );
  }

  const withReason = content.match(REJECTED_WITH_REASON);
  if (withReason) {
    const reason = withReason[1].trim();
    return (
      <div
        className={cn(
          "aui-approval-status flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-800",
        )}
        data-testid="approval-status-rejected"
      >
        <XIcon className="size-3.5" />
        <span className="font-medium">
          {t("approval.status.rejected_with_reason", { reason })}
        </span>
      </div>
    );
  }

  if (content === REJECTED_BARE) {
    return (
      <div
        className={cn(
          "aui-approval-status flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-800",
        )}
        data-testid="approval-status-rejected"
      >
        <XIcon className="size-3.5" />
        <span className="font-medium">{t("approval.status.rejected")}</span>
      </div>
    );
  }

  return null;
});
